import { db, storage } from "../firebase/firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const SCHOOLS = "schools";

/**
 * A school is stored ONCE and reused by every product. Deliberately not one
 * Stripe product per school — 400 schools x 3 items x 4 sizes would be 4,800
 * products to maintain. The school is a selection made at purchase time and
 * carried onto the order.
 */

export const LOGO_STATUS = {
  ON_FILE: "onFile",   // artwork ready — order goes straight to fulfillment
  PENDING: "pending",  // sellable, but the buyer must send artwork after checkout
};

/**
 * Key for a per-school product mockup. A pre-composed photo of the actual
 * shirt with the actual logo on it always beats a CSS overlay — but it means
 * one upload per school PER COLOR, so both are supported (see
 * resolveProductImage below).
 */
export function mockupKey(productId, colorKey) {
  return `${productId}::${colorKey || "default"}`;
}

/**
 * Which image to show for a product once a school is chosen. Order matters:
 *
 *   1. A real mockup uploaded for this school + this color  — best
 *   2. A real mockup uploaded for this school, any color    — good
 *   3. The generic product/variant photo                    — always works
 *
 * Nothing ever breaks: a school with no mockups just shows the plain garment,
 * which is why you can list a school the moment you have its name.
 */
export function resolveProductImage({ product, variant, school }) {
  const generic = variant?.image || product?.image || null;
  if (!school?.productImages) return generic;

  return (
    school.productImages[mockupKey(product.id, variant?.colorKey)] ||
    school.productImages[mockupKey(product.id, "default")] ||
    generic
  );
}

export function schoolDocId(state, name) {
  const slug = `${state}-${name}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return slug.slice(0, 180);
}

export async function listSchools({ approvedOnly = false } = {}) {
  const base = collection(db, SCHOOLS);
  const q = approvedOnly
    ? query(base, where("approved", "==", true))
    : query(base, orderBy("state", "asc"));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return rows.sort(
    (a, b) =>
      (a.state || "").localeCompare(b.state || "") ||
      (a.name || "").localeCompare(b.name || "")
  );
}

export async function getSchool(schoolId) {
  const snap = await getDoc(doc(db, SCHOOLS, schoolId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function upsertSchool(school) {
  const id = school.id || schoolDocId(school.state, school.name);
  await setDoc(
    doc(db, SCHOOLS, id),
    {
      name: school.name || "",
      state: (school.state || "").toUpperCase(),
      district: school.district || "",
      mascot: school.mascot || "",
      logoUrl: school.logoUrl || "",
      logoStatus: school.logoUrl ? LOGO_STATUS.ON_FILE : LOGO_STATUS.PENDING,
      // Districts commonly require a license to sell merchandise bearing a
      // school name, mascot or logo. Nothing is offered for sale until this
      // is switched on, so an unlicensed district can never reach checkout.
      approved: school.approved === true,
      licenseNote: school.licenseNote || "",
      updatedAt: serverTimestamp(),
      ...(school.createdAt ? {} : { createdAt: serverTimestamp() }),
    },
    { merge: true }
  );
  return id;
}

export async function deleteSchool(schoolId) {
  return deleteDoc(doc(db, SCHOOLS, schoolId));
}

export async function setSchoolApproved(schoolId, approved) {
  return updateDoc(doc(db, SCHOOLS, schoolId), {
    approved: !!approved,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Upload a finished mockup — the shirt/tote/pendant photo with this school's
 * artwork already on it. Stored per product + color on the school document
 * (a bounded map: a handful of products x a handful of colors), so the
 * storefront needs no extra reads — it already has the school loaded.
 */
export async function uploadSchoolMockup(schoolId, productId, colorKey, file) {
  const safe = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
  const storageRef = ref(
    storage,
    `schoolLogos/${schoolId}/mockups/${productId}/${safe}`
  );
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);

  await setDoc(
    doc(db, SCHOOLS, schoolId),
    {
      productImages: { [mockupKey(productId, colorKey)]: url },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return url;
}

export async function removeSchoolMockup(schoolId, productId, colorKey) {
  // Firestore has no "delete a map key" in a merge write, so the map is
  // rewritten without it.
  const snap = await getDoc(doc(db, SCHOOLS, schoolId));
  if (!snap.exists()) return;

  const images = { ...(snap.data().productImages || {}) };
  delete images[mockupKey(productId, colorKey)];

  await updateDoc(doc(db, SCHOOLS, schoolId), {
    productImages: images,
    updatedAt: serverTimestamp(),
  });
}

export async function uploadSchoolLogo(schoolId, file) {
  const safe = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
  const storageRef = ref(storage, `schoolLogos/${schoolId}/${safe}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await updateDoc(doc(db, SCHOOLS, schoolId), {
    logoUrl: url,
    logoStatus: LOGO_STATUS.ON_FILE,
    updatedAt: serverTimestamp(),
  });
  return url;
}

/**
 * CSV import. Header row required. Recognised columns:
 *   name, state, district, mascot, logoUrl, approved
 */
export function parseSchoolsCsv(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];

  const headers = lines[0]
    .split(",")
    .map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());

  return lines
    .slice(1)
    .map((line) => {
      const cells = line
        .split(",")
        .map((c) => c.trim().replace(/^"|"$/g, ""));
      const row = {};
      headers.forEach((h, i) => {
        row[h] = cells[i] || "";
      });
      if (!row.name || !row.state) return null;
      return {
        name: row.name,
        state: row.state.toUpperCase(),
        district: row.district || "",
        mascot: row.mascot || "",
        logoUrl: row.logourl || row.logo || "",
        approved: /^(true|yes|1|y)$/i.test(row.approved || ""),
      };
    })
    .filter(Boolean);
}

export async function importSchools(rows) {
  let batch = writeBatch(db);
  let ops = 0;
  let written = 0;

  for (const row of rows) {
    const id = schoolDocId(row.state, row.name);
    batch.set(
      doc(db, SCHOOLS, id),
      {
        name: row.name,
        state: row.state,
        district: row.district || "",
        mascot: row.mascot || "",
        logoUrl: row.logoUrl || "",
        logoStatus: row.logoUrl ? LOGO_STATUS.ON_FILE : LOGO_STATUS.PENDING,
        approved: row.approved === true,
        source: "csv-import",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    ops += 1;
    written += 1;

    if (ops >= 400) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }

  if (ops > 0) await batch.commit();
  return { written };
}

/**
 * Best-effort match of a signed-in user's profile to a school record, so the
 * storefront can pre-select it. Matching on name is fuzzy on purpose —
 * profiles hold free text like "Park Hill Elem." against "Park Hill
 * Elementary School".
 */
export function matchUserSchool(schools, userState, userSchoolName) {
  if (!userSchoolName) return null;

  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\b(elementary|middle|high|school|academy|elem|jr|sr)\b/g, "")
      .replace(/[^a-z0-9]+/g, "");

  const target = norm(userSchoolName);
  if (!target) return null;

  const pool = userState
    ? schools.filter((s) => (s.state || "") === userState)
    : schools;

  return (
    pool.find((s) => norm(s.name) === target) ||
    pool.find((s) => norm(s.name).includes(target) || target.includes(norm(s.name))) ||
    null
  );
}
